import { EventBus } from "../EventBus.js";
import {
  QUICKSAVE_SLOT_ID,
  save,
  load,
  type SaveSlotInput,
  type SaveSlotSummary,
} from "./PersistenceManager.js";
import type { LoadSaveFileResult } from "./SaveFile.js";

/**
 * Quicksave (F5) / Quickload (F9) hotkeys (P06).
 *
 * Both run against the reserved `quicksave` slot. Every attempt — success
 * or failure — emits a `ui:combat_log_push` so the player sees a
 * confirmation in the feed without needing to open a menu.
 *
 * Gated via `isBlocked()`: when `true` (combat active, dialogue active,
 * etc. — see P08) the hotkey is treated as a no-op with a feed line.
 */

export type QuicksaveAction = "save" | "load";

export interface QuicksaveHotkeysOptions {
  readonly inputProvider: () => SaveSlotInput;
  /** Return true to refuse a quicksave/quickload attempt (P08). */
  readonly isBlocked?: () => boolean;
  /** Reason string surfaced in the blocked-feed-line. Default: "not right now". */
  readonly blockedReason?: () => string;
  readonly saveImpl?: (slotId: string, input: SaveSlotInput) => Promise<SaveSlotSummary>;
  readonly loadImpl?: (slotId: string) => Promise<LoadSaveFileResult>;
  /** Optional hooks for UI layers that need more than a feed message. */
  readonly onSaveSuccess?: (summary: SaveSlotSummary) => void;
  readonly onLoadSuccess?: (result: LoadSaveFileResult) => void;
  readonly onError?: (action: QuicksaveAction, error: unknown) => void;
}

export interface QuicksaveHotkeys {
  readonly dispose: () => void;
  readonly quicksave: () => Promise<void>;
  readonly quickload: () => Promise<void>;
}

function feed(message: string): void {
  EventBus.emit("ui:combat_log_push", { message });
}

export function createQuicksaveHotkeys(
  options: QuicksaveHotkeysOptions,
): QuicksaveHotkeys {
  const saveImpl = options.saveImpl ?? save;
  const loadImpl = options.loadImpl ?? load;

  const blocked = (): boolean => options.isBlocked?.() ?? false;
  const blockedReason = (): string =>
    options.blockedReason?.() ?? "not right now";

  const quicksave = async (): Promise<void> => {
    if (blocked()) {
      feed(`Can't quicksave — ${blockedReason()}.`);
      return;
    }
    try {
      const summary = await saveImpl(QUICKSAVE_SLOT_ID, options.inputProvider());
      feed(`Quicksaved: ${summary.displayName}`);
      options.onSaveSuccess?.(summary);
    } catch (err) {
      feed(`Quicksave failed: ${describeError(err)}`);
      options.onError?.("save", err);
    }
  };

  const quickload = async (): Promise<void> => {
    if (blocked()) {
      feed(`Can't quickload — ${blockedReason()}.`);
      return;
    }
    try {
      const result = await loadImpl(QUICKSAVE_SLOT_ID);
      feed("Quickload restored.");
      options.onLoadSuccess?.(result);
    } catch (err) {
      feed(`Quickload failed: ${describeError(err)}`);
      options.onError?.("load", err);
    }
  };

  const onKeyDown = (e: KeyboardEvent): void => {
    if (e.defaultPrevented) return;
    if (e.key === "F5") {
      e.preventDefault();
      void quicksave();
    } else if (e.key === "F9") {
      e.preventDefault();
      void quickload();
    }
  };

  window.addEventListener("keydown", onKeyDown);

  return {
    dispose: () => window.removeEventListener("keydown", onKeyDown),
    quicksave,
    quickload,
  };
}

function describeError(err: unknown): string {
  if (err instanceof Error) return err.message;
  return String(err);
}
